import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../lib/api";
import Logo from "../components/Logo";
import {
  Truck, MapPin, Calendar, PaperPlaneRight, ChatCircleDots, Package,
  Warning, CheckCircle, Camera, Trash, CircleNotch, ClockCounterClockwise,
  ArrowRight, X
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending: "Booked",
  assigned: "Assigned to you",
  arrived_pickup: "At pickup",
  in_transit: "In transit",
  arrived_delivery: "At delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLOR = {
  pending: "text-neutral-400 border-neutral-500/30 bg-neutral-500/5",
  assigned: "text-[#007AFF] border-[#007AFF]/30 bg-[#007AFF]/5",
  arrived_pickup: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/5",
  in_transit: "text-[#FF5A1F] border-[#FF5A1F]/30 bg-[#FF5A1F]/5",
  arrived_delivery: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/5",
  delivered: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5",
  cancelled: "text-red-400 border-red-400/30 bg-red-400/5",
};

// Driver-side transitions — must match backend DRIVER_STATUS_TRANSITIONS
const NEXT_STATUS = {
  assigned: { status: "arrived_pickup", label: "Arrived at Pickup", verb: "arriving at pickup" },
  arrived_pickup: { status: "in_transit", label: "Picked Up · In Transit", verb: "loaded and rolling" },
  in_transit: { status: "arrived_delivery", label: "Arrived at Delivery", verb: "arriving at delivery" },
  arrived_delivery: { status: "delivered", label: "Delivered", verb: "marking delivered" },
};

export default function DriverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await client.get(`/driver-portal/${token}`);
      setData(r.data);
    } catch (e) {
      setErr("This driver link is invalid or expired. Contact your dispatcher for a new link.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000); // poll for new messages every 15s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  const sendReply = async (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await client.post(`/driver-portal/${token}/reply`, { message: text });
      setReply("");
      toast.success("Sent to dispatch");
      load();
    } catch {
      toast.error("Couldn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm" data-testid="driver-portal-loading">
        Loading your portal...
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen relative noise-bg flex items-center justify-center px-6" data-testid="driver-portal-error">
        <div className="card-surface p-8 max-w-md w-full text-center">
          <Warning size={32} weight="regular" className="mx-auto text-[#F59E0B] mb-4" />
          <h2 className="font-display text-xl font-semibold">Link not found</h2>
          <p className="text-neutral-400 mt-2 text-sm">{err}</p>
          <Link to="/" className="inline-block mt-6 text-xs text-[#FF5A1F] hover:text-[#FF7038]">← Back to FreightPilot AI</Link>
        </div>
      </div>
    );
  }

  const { driver, company, loads = [], messages = [] } = data;
  const activeLoads = loads.filter((l) => l.status !== "delivered" && l.status !== "cancelled");
  const completedLoads = loads.filter((l) => l.status === "delivered");

  return (
    <div className="min-h-screen relative noise-bg" data-testid="driver-portal">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/">
            <Logo variant="sm" />
          </Link>
          <div className="text-right">
            <div className="text-xs text-neutral-500 uppercase tracking-widest">Driver Portal</div>
            <div className="text-xs text-neutral-300 font-medium mt-0.5">{company}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 pb-40">
        {/* Driver banner */}
        <div className="card-surface p-5 mb-6" data-testid="driver-info-card">
          <div className="overline mb-2">WELCOME</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight leading-tight">
            Hey, {driver.name?.split(" ")[0] || "driver"}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-neutral-400 flex-wrap">
            {driver.truck_number && (
              <div className="flex items-center gap-1.5">
                <Truck size={14} className="text-[#FF5A1F]" />
                Truck {driver.truck_number}
              </div>
            )}
            <div className="flex items-center gap-1.5 capitalize">
              <span className={`w-1.5 h-1.5 rounded-full ${driver.status === "available" ? "bg-emerald-400" : driver.status === "on_load" ? "bg-[#FF5A1F]" : "bg-neutral-500"}`} />
              {(driver.status || "unknown").replace("_", " ")}
            </div>
          </div>
        </div>

        {/* Active loads */}
        <section className="mb-8" data-testid="driver-active-loads">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">YOUR LOADS · {activeLoads.length} ACTIVE</div>
          </div>

          {activeLoads.length === 0 ? (
            <div className="card-surface p-8 text-center">
              <Package size={28} className="mx-auto text-neutral-600 mb-3" />
              <div className="text-sm text-neutral-400">No active loads assigned yet.</div>
              <div className="text-xs text-neutral-600 mt-1">Dispatch will notify you when one's ready.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {activeLoads.map((l) => (
                <LoadCard key={l.id} load={l} token={token} onChange={load} />
              ))}
            </div>
          )}

          {completedLoads.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-300">
                Show {completedLoads.length} completed load{completedLoads.length > 1 ? "s" : ""}
              </summary>
              <div className="space-y-3 mt-3">
                {completedLoads.map((l) => (
                  <LoadCard key={l.id} load={l} token={token} onChange={load} muted />
                ))}
              </div>
            </details>
          )}
        </section>

        {/* Messages */}
        <section className="mb-8" data-testid="driver-messages">
          <div className="overline mb-3 flex items-center gap-2">
            <ChatCircleDots size={14} className="text-[#FF5A1F]" />
            DISPATCH MESSAGES
          </div>

          {messages.length === 0 ? (
            <div className="card-surface p-6 text-center">
              <div className="text-sm text-neutral-400">No messages yet.</div>
              <div className="text-xs text-neutral-600 mt-1">Reply below to start a conversation with dispatch.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map((m) => (
                <MessageBubble key={m.id} msg={m} companyName={company} driverName={driver.name} />
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </section>
      </main>

      {/* Sticky reply composer */}
      <div className="fixed bottom-0 inset-x-0 z-20 border-t border-white/10 backdrop-blur-md bg-[#0A0A0A]/95">
        <form onSubmit={sendReply} className="max-w-2xl mx-auto px-5 py-3 flex items-end gap-2" data-testid="driver-reply-form">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Message dispatch..."
            rows={1}
            className="flex-1 bg-[#141414] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F] resize-none max-h-32"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendReply(e);
              }
            }}
            data-testid="driver-reply-input"
          />
          <button
            type="submit"
            disabled={sending || !reply.trim()}
            className="h-10 w-10 flex items-center justify-center bg-[#FF5A1F] text-black rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FF7038] transition-colors shrink-0"
            data-testid="driver-reply-submit"
            aria-label="Send message"
          >
            <PaperPlaneRight size={16} weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
}

function LoadCard({ load, token, onChange, muted }) {
  const badge = STATUS_COLOR[load.status] || STATUS_COLOR.pending;
  const next = NEXT_STATUS[load.status];
  const history = load.status_history || [];
  const photos = load.pod_photos || [];
  const [updating, setUpdating] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const fileInputRef = useRef(null);

  const advance = async (confirmed = false) => {
    if (!next || updating) return;
    // For "Delivered" step, require confirmation
    if (next.status === "delivered" && !confirmed) {
      if (!window.confirm(`Mark load ${load.load_number} as DELIVERED?\n\nThis is the final status and cannot be undone from the driver portal.`)) return;
    }
    setUpdating(true);
    try {
      await client.post(`/driver-portal/${token}/load/${load.id}/status`, {
        status: next.status,
        note: note.trim() || undefined,
      });
      toast.success(`Status updated: ${STATUS_LABEL[next.status]}`);
      setNote("");
      setShowNote(false);
      onChange && onChange();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update. Try again.";
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick a photo (JPG or PNG).");
      return;
    }
    compressAndUpload(file);
  };

  const compressAndUpload = async (file) => {
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, 1600, 0.72);
      if (dataUrl.length > 1_450_000) {
        // second pass with tighter quality if still too large
        const smaller = await compressImage(file, 1200, 0.6);
        await sendPhoto(smaller);
      } else {
        await sendPhoto(dataUrl);
      }
    } catch (e) {
      toast.error("Couldn't process that photo. Try another.");
    } finally {
      setUploading(false);
    }
  };

  const sendPhoto = async (dataUrl) => {
    try {
      await client.post(`/driver-portal/${token}/load/${load.id}/photo`, {
        data_url: dataUrl,
        caption: "",
      });
      toast.success("Photo uploaded to dispatch");
      onChange && onChange();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Try again.";
      toast.error(msg);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await client.delete(`/driver-portal/${token}/load/${load.id}/photo/${photoId}`);
      toast.success("Photo removed");
      setViewer(null);
      onChange && onChange();
    } catch {
      toast.error("Couldn't delete. Try again.");
    }
  };

  return (
    <div className={`card-surface p-4 ${muted ? "opacity-60" : ""}`} data-testid={`driver-load-${load.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Load</div>
          <div className="font-display text-lg font-semibold text-white truncate">#{load.load_number}</div>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border ${badge} shrink-0`}>
          {STATUS_LABEL[load.status] || load.status}
        </div>
      </div>

      <div className="flex items-start gap-2 text-sm text-neutral-200 mb-2">
        <MapPin size={16} className="text-[#FF5A1F] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="truncate">{load.origin}</div>
          <div className="text-neutral-600 text-xs my-0.5">↓</div>
          <div className="truncate">{load.destination}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-neutral-400 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="flex items-center gap-1 text-neutral-600 mb-0.5">
            <Calendar size={11} /> PICKUP
          </div>
          <div className="text-neutral-300">{load.pickup_date || "—"}</div>
        </div>
        <div>
          <div className="flex items-center gap-1 text-neutral-600 mb-0.5">
            <Calendar size={11} /> DELIVERY
          </div>
          <div className="text-neutral-300">{load.delivery_date || "—"}</div>
        </div>
      </div>

      {load.customer && (
        <div className="text-xs text-neutral-500 mt-3">
          Customer: <span className="text-neutral-300">{load.customer}</span>
        </div>
      )}

      {/* Status advance action */}
      {next && !muted && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
          {showNote && (
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              rows={2}
              placeholder="Add a note for dispatch (optional)..."
              className="w-full bg-[#141414] border border-white/15 rounded-lg px-3 py-2 text-xs text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F] resize-none"
              data-testid={`driver-load-note-${load.id}`}
            />
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => advance(false)}
              disabled={updating}
              data-testid={`driver-status-btn-${load.id}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5A1F] text-black text-sm font-semibold rounded-lg px-4 py-2.5 disabled:opacity-50 hover:bg-[#FF7038] transition-colors"
            >
              {updating ? (
                <>
                  <CircleNotch size={14} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <ArrowRight size={14} weight="bold" />
                  {next.label}
                </>
              )}
            </button>
            <button
              onClick={() => setShowNote((v) => !v)}
              className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300 px-2"
              data-testid={`driver-note-toggle-${load.id}`}
            >
              {showNote ? "Hide note" : "+ Note"}
            </button>
          </div>
        </div>
      )}

      {/* Photos */}
      {!muted && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="overline flex items-center gap-1">
              <Camera size={11} /> PROOF PHOTOS {photos.length > 0 && `· ${photos.length}`}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || photos.length >= 8}
              className="text-[10px] uppercase tracking-widest text-[#FF5A1F] hover:text-[#FF7038] disabled:opacity-40 inline-flex items-center gap-1"
              data-testid={`driver-photo-upload-${load.id}`}
            >
              {uploading ? (
                <><CircleNotch size={11} className="animate-spin" /> Uploading</>
              ) : (
                <><Camera size={11} /> Add Photo</>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPickFile}
              data-testid={`driver-photo-input-${load.id}`}
            />
          </div>
          {photos.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setViewer(p)}
                  className="aspect-square rounded-lg overflow-hidden bg-[#141414] border border-white/10 hover:border-[#FF5A1F] transition-colors"
                  data-testid={`driver-photo-thumb-${p.id}`}
                >
                  <img src={p.data_url} alt="POD" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          ) : (
            <div className="text-xs text-neutral-600">No photos yet. Snap a POD, BOL, or damage photo.</div>
          )}
        </div>
      )}

      {/* Timeline toggle */}
      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <button
            onClick={() => setShowTimeline((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1"
            data-testid={`driver-timeline-toggle-${load.id}`}
          >
            <ClockCounterClockwise size={11} />
            {showTimeline ? "Hide" : "Show"} timeline ({history.length})
          </button>
          {showTimeline && (
            <ol className="mt-3 space-y-2" data-testid={`driver-timeline-${load.id}`}>
              {history.slice().reverse().map((h, i) => (
                <li key={i} className="flex gap-3 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-neutral-200">{STATUS_LABEL[h.status] || h.status}</div>
                    {h.note && <div className="text-neutral-500 mt-0.5">"{h.note}"</div>}
                    <div className="text-[10px] text-neutral-600 mt-0.5">
                      {new Date(h.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Photo lightbox */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col p-4"
          onClick={() => setViewer(null)}
          data-testid="driver-photo-viewer"
        >
          <div className="flex justify-end gap-2 mb-4">
            <button
              onClick={(e) => { e.stopPropagation(); deletePhoto(viewer.id); }}
              className="p-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/40"
              data-testid="driver-photo-delete"
              aria-label="Delete photo"
            >
              <Trash size={18} />
            </button>
            <button
              onClick={() => setViewer(null)}
              className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <img
              src={viewer.data_url}
              alt="POD"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="text-center text-xs text-neutral-500 mt-3">
            Uploaded {viewer.uploaded_at ? new Date(viewer.uploaded_at).toLocaleString() : ""}
          </div>
        </div>
      )}
    </div>
  );
}

// Client-side image compression using canvas
function compressImage(file, maxDim = 1600, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MessageBubble({ msg, companyName, driverName }) {
  const isDriver = msg.sender === "driver";
  return (
    <div className={`flex ${isDriver ? "justify-end" : "justify-start"}`} data-testid={`driver-msg-${msg.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${isDriver ? "bg-[#FF5A1F] text-black rounded-br-md" : "bg-[#141414] border border-white/10 text-neutral-100 rounded-bl-md"}`}>
        <div className={`text-[10px] uppercase tracking-widest mb-1 ${isDriver ? "text-black/60" : "text-neutral-500"}`}>
          {isDriver ? driverName || "You" : companyName || "Dispatch"}
        </div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</div>
        <div className={`text-[10px] mt-1 ${isDriver ? "text-black/50" : "text-neutral-600"}`}>
          {msg.created_at ? new Date(msg.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
        </div>
      </div>
    </div>
  );
}
